import { Grid } from '../../../../../Common/Grid'
import { Typography, Button } from '@fullscreendigital/carrefour-dex-fe'
import { TitleHeader } from '../../../../Common/TitleHeader'
import { useNotification } from '../../../../../../../hooks'
import { useQuery } from 'react-query'
import { makeCoreGetRequest } from "../../../../../../../utils/api";
import { formatErrorMessage } from '../../../../../../../utils/formatting'
import { CircularProgress, Paper } from "@mui/material"
import React from "react"
import { useNavigate, useParams } from "react-router-dom"
import { displayOrDash } from "../../../../helpers";

type Truck = {
  id: number
  shopName: string
  truckNumber: string
  supportCounter: number
  sealList: string[]
  registeredDate: string
  createdBy: string
  driverName?: string | null
  transportCode?: string | null
}

const isMissing = (v: unknown) =>
  v == null || (typeof v === 'string' && v.trim() === '')

const RegisterTruckStep4 = () => {
  const { showNotification } = useNotification()
  const navigate = useNavigate()
  const { id = '' } = useParams<{ id: string }>()

  const fetchTruck = async (): Promise<Truck | null> => {
    try {
      const res = await makeCoreGetRequest(
        `/shop-reception-mobile/truck/hub/registered/${id}`,
        null
      )
      return (res?.data ?? res) as Truck
    } catch (err: any) {
      if (err?.response?.status === 404) return null
      throw err
    }
  }

  const {
    data: truck,
    isLoading,
    isFetching,
    refetch,
  } = useQuery<Truck | null, Error>({
    queryKey: ['registeredTruckDetails', id],
    queryFn: fetchTruck,
    enabled: !!id,
    placeholderData: null,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 404) return false
      return failureCount < 2
    },
    onError: (error) => {
      showNotification({
        message: `Error while fetching truck. ${formatErrorMessage(error)}`,
        type: 'error',
      })
    },
  })

  if (isLoading) {
    return <CircularProgress color="primary" style={{ margin: '40px auto', display: 'block' }} disableShrink />
  }

  const needsRefresh = !!truck && (isMissing(truck.driverName) || isMissing(truck.transportCode))

  const items = [
    <TitleHeader key="title" title="CAMIOANE EXPEDIATE" />,
    (truck === null || truck === undefined) ? (
      <Paper key="no-truck" elevation={3} style={{ margin: '10px', padding: '20px', textAlign: 'center' }}>
        <Typography variant="light" component="p" style={{ textTransform: 'uppercase', marginBottom: 16 }}>
          Nu existǎ camioane expediate.
        </Typography>
        <Button onClick={() => navigate('/goodsReceptionHub')} variant="outlined" label="Mergi la Home" />
      </Paper>
    ) : (
      <Paper key={id || 'truck'} elevation={3} style={{ margin: '10px', padding: '20px' }}>
        <Typography variant='medium' component='p'>Magazin(e) destinație: {displayOrDash(truck.shopName)}</Typography>
        <Typography variant='medium' component='p'>Număr înmatriculare: {displayOrDash(truck.truckNumber)}</Typography>
        <Typography variant='medium' component='p'>Suporti în camion: {displayOrDash(truck.supportCounter)}</Typography>

        <Typography variant="medium" component="p">
          Sigiliu (sigilii): {truck.sealList?.length ? truck.sealList.join(' / ') : '-'}
        </Typography>

        <Typography variant='medium' component='p'>Dată înregistrare: {displayOrDash(truck.registeredDate)}</Typography>
        <Typography variant='medium' component='p'>Operator: {displayOrDash(truck.createdBy)}</Typography>

        <Typography variant='medium' component='p'>Șofer: {displayOrDash(truck.driverName)}</Typography>
        <Typography variant='medium' component='p'>Cod transport: {displayOrDash(truck.transportCode)}</Typography>

        {needsRefresh && (
          <div style={{ marginTop: 16 }}>
            <Button
              variant="contained"
              onClick={() => refetch()}
              disabled={isFetching}
              label={isFetching ? 'Se actualizează...' : 'Reîmprospătează'}
            />
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <Button onClick={() => navigate('/goodsReceptionHub')} variant="outlined" label="Mergi la Home" />
        </div>
      </Paper>
    ),
  ]

  return (
    <Grid gridContainerStyle={{ marginTop: '20px' }} maxWidth="xs" items={items} itemsPerRow={1} />
  )
}

export default RegisterTruckStep4
